import {
  BackEndValueType,
  CommonHelperFunctions,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IKTypeField,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import InfoSvg from "../../../../svgs/small-icons/info.svg";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { ITreeSelectNode, KuikaTreeSelect } from "../../../ui-kit/kuika-tree-select";
import { IActionPanelStatesCanBeUpdatedFromChild, SelectboxInputParameter } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

// Delete strategy types - maps to backend boolean
export enum EDeleteStrategy {
  CleanAndInsert = "CleanAndInsert",
  InsertOrUpdate = "InsertOrUpdate"
}

// Action result type classification
export enum EActionResultType {
  Object = "Object",
  String = "String",
  Invalid = "Invalid" // Number, Boolean, Null - not selectable
}

export interface ISaveActionResultSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

export interface ISaveActionResultSymbolsGroupState {
  selectedActionId: string | undefined;
  selectedActionResultType: EActionResultType;
  deleteStrategy: EDeleteStrategy;
  vectorFields: string[];
  metadataFields: string[];
  primaryKeys: string[];
  jqSchema: string;
  metadataJqSchema: string;
  isSaved: boolean;
  validationError: string | null;
}

/**
 * SaveActionResultSymbolsGroup - Dynamic action panel for SaveActionResult
 *
 * This panel allows users to store the result of another action into the
 * RAG / Vector Search system (Qdrant).
 *
 * Key behaviors:
 * - Filters out actions returning primitives (number, boolean, null)
 * - Dynamically shows/hides fields based on action result type and delete strategy
 * - Builds JQ schema from selected vector/metadata fields
 * - Validates required fields before submission
 */
class SaveActionResultSymbolsGroup extends PureSdComponent<
  ISaveActionResultSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISaveActionResultSymbolsGroupState
> {
  constructor(props) {
    super(props);
    this.state = {
      selectedActionId: undefined,
      selectedActionResultType: EActionResultType.Invalid,
      deleteStrategy: EDeleteStrategy.CleanAndInsert,
      vectorFields: [],
      metadataFields: [],
      primaryKeys: [],
      jqSchema: "",
      metadataJqSchema: "",
      isSaved: true, // Initially true if loading existing config
      validationError: null
    };
  }

  componentDidMount(): void {
    this.initializeFromExistingParameters();
  }

  /**
   * Initialize state from existing action parameters if editing
   */
  initializeFromExistingParameters = () => {
    const actionIdParam = this.props.parameters.find((p) => p.name === "actionId");
    const nameParam = this.props.parameters.find((p) => p.name === "name");
    const deleteOlderRecordsParam = this.props.parameters.find((p) => p.name === "deleteOlderRecords");
    const primaryKeysParam = this.props.parameters.find((p) => p.name === "primaryKeys");
    const jqSchemaParam = this.props.parameters.find((p) => p.name === "jQSchema");
    const metadataJqSchemaParam = this.props.parameters.find((p) => p.name === "metadataJQSchema");

    if (actionIdParam?.symbol?.fixedValue) {
      const actionDefinitionId = actionIdParam.symbol.fixedValue as string;

      // Find action instance by:
      // 1. First try to match using the tag from the name parameter
      // 2. Fall back to finding any instance with matching actionDefinitionId
      const screenActions = this.getSelectableActionInstances();
      let actionInstance: IAction | undefined;

      // Try to extract tag from name parameter (format: project_page_tag)
      if (nameParam?.symbol?.fixedValue) {
        const nameParts = (nameParam.symbol.fixedValue as string).split("_");
        if (nameParts.length >= 3) {
          // Tag is everything after project_page_ (in case tag itself contains underscores)
          const projectName = this.props.sd.project?.name || "";
          const pageName = this.props.sd.sdName || "";
          const prefix = `${projectName}_${pageName}_`;
          const name = nameParam.symbol.fixedValue as string;
          if (name.startsWith(prefix)) {
            const extractedTag = name.substring(prefix.length);
            // Find instance by tag and definition ID
            actionInstance = screenActions.find(
              (a) => a.actionDefinitionId === actionDefinitionId && (a.tag === extractedTag || a.id === extractedTag)
            );
          }
        }
      }

      // Fallback: find first instance with matching actionDefinitionId
      if (!actionInstance) {
        actionInstance = screenActions.find((a) => a.actionDefinitionId === actionDefinitionId);
      }

      if (actionInstance) {
        const actionDef = this.getActionDefinitionForInstance(actionInstance);
        if (actionDef) {
          const resultType = this.classifyActionResultType(actionDef);
          const deleteStrategy =
            deleteOlderRecordsParam?.symbol?.fixedValue === "true"
              ? EDeleteStrategy.CleanAndInsert
              : EDeleteStrategy.InsertOrUpdate;

          // Parse primary keys from comma-separated string
          const primaryKeys = primaryKeysParam?.symbol?.fixedValue
            ? (primaryKeysParam.symbol.fixedValue as string).split(",").filter(Boolean)
            : [];

          // Parse vector fields from jqSchema
          const jqSchema = (jqSchemaParam?.symbol?.fixedValue as string) || "";
          const vectorFields = this.parseFieldsFromJqSchema(jqSchema);

          // Parse metadata fields from metadataJQSchema
          const metadataJqSchema = (metadataJqSchemaParam?.symbol?.fixedValue as string) || "";
          const metadataFields = this.parseFieldsFromJqSchema(metadataJqSchema);

          this.setState({
            selectedActionId: actionInstance.id, // Store action INSTANCE id
            selectedActionResultType: resultType,
            deleteStrategy,
            primaryKeys,
            jqSchema,
            metadataJqSchema,
            vectorFields,
            metadataFields,
            isSaved: true, // Loaded from existing config, so it's saved
            validationError: null
          });
        }
      }
    }
  };

  /**
   * Parse field names from a JQ schema string
   * Example: ".[] | {name, description, id}" => ["name", "description", "id"]
   */
  parseFieldsFromJqSchema = (jqSchema: string): string[] => {
    if (!jqSchema || jqSchema === ".") return [];

    // Match the content inside curly braces: .[] | {field1, field2, field3}
    const match = jqSchema.match(/\{([^}]+)\}/);
    if (!match || !match[1]) return [];

    // Split by comma and trim whitespace
    return match[1]
      .split(",")
      .map((field) => field.trim())
      .filter(Boolean);
  };

  /**
   * Get all action definitions (for internal use - returnTypeModel, classification, etc.)
   */
  getActionDefinitions = (): IActionDefinitionDto[] => {
    return this.props.sd.actionDefinitions || [];
  };

  /**
   * Get action definition by ID
   */
  getActionDefinition = (actionDefinitionId: string): IActionDefinitionDto | undefined => {
    return this.getActionDefinitions().find((a) => a.id === actionDefinitionId);
  };

  /**
   * Get action definition for an action instance
   */
  getActionDefinitionForInstance = (actionInstance: IAction): IActionDefinitionDto | undefined => {
    if (!actionInstance.actionDefinitionId) return undefined;
    return this.getActionDefinition(actionInstance.actionDefinitionId);
  };

  /**
   * Get all action instances that are valid for SaveActionResult
   * Excludes actions returning primitives (number, boolean, null, void)
   * Only includes actions from the current screen
   * Excludes the current SaveActionSource action itself
   */
  getSelectableActionInstances = (): IAction[] => {
    // Get all action instances from the current screen
    const screenActions = this.props.sd.getDefinedActions();
    const currentActionId = this.props.action.id;

    return screenActions.filter((actionInstance) => {
      // Exclude the current SaveActionSource action itself
      if (actionInstance.id === currentActionId) return false;

      // Get the action definition to check return type
      const actionDef = this.getActionDefinitionForInstance(actionInstance);
      if (!actionDef) return false;

      // Skip actions without return type
      if (!actionDef.returnTypeName) return false;

      // Classify return type
      const resultType = this.classifyActionResultType(actionDef);

      // Only allow Object and String types
      return resultType !== EActionResultType.Invalid;
    });
  };

  /**
   * Get action definitions that are valid for SaveActionResult (legacy - for backward compatibility)
   * @deprecated Use getSelectableActionInstances instead
   */
  getSelectableActions = (): IActionDefinitionDto[] => {
    const screenActions = this.props.sd.getDefinedActions();
    const screenActionDefIds = new Set(screenActions.map((a) => a.actionDefinitionId));
    const currentActionDefId = this.props.action.actionDefinitionId;

    return this.getActionDefinitions().filter((action) => {
      if (action.id === currentActionDefId) return false;
      if (!screenActionDefIds.has(action.id)) return false;
      if (!action.returnTypeName) return false;
      const resultType = this.classifyActionResultType(action);
      return resultType !== EActionResultType.Invalid;
    });
  };

  /**
   * Classify an action's return type into Object, String, or Invalid
   */
  classifyActionResultType = (action: IActionDefinitionDto): EActionResultType => {
    const typeName = action.returnTypeName?.toLowerCase();

    if (!typeName || typeName === "void") {
      return EActionResultType.Invalid;
    }

    // Check for primitives that should not be selectable
    const invalidTypes = [
      "boolean",
      "bool",
      "int32",
      "int64",
      "int16",
      "integer",
      "int",
      "double",
      "decimal",
      "float",
      "number",
      "guid"
    ];

    if (invalidTypes.includes(typeName)) {
      return EActionResultType.Invalid;
    }

    // String type
    if (typeName === "string") {
      return EActionResultType.String;
    }

    // If it has returnTypeModel with elements, it's an Object
    if (action.returnTypeModel?.elements && action.returnTypeModel.elements.length > 0) {
      return EActionResultType.Object;
    }

    // Check if it's a complex type (not primitive)
    if (!CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(action.returnTypeName)) {
      return EActionResultType.Object;
    }

    return EActionResultType.String;
  };

  /**
   * Build tree data for field selection from returnTypeModel
   */
  buildFieldTreeData = (elements: IKTypeField[], parentPath: string = "", rootBranch?: string): ITreeSelectNode[] => {
    return elements.map((element) => {
      const currentPath = parentPath ? `${parentPath}.${element.name}` : element.name;
      const hasChildren = element.elementTypeModel?.elements && element.elementTypeModel.elements.length > 0;

      // Determine if this node should be disabled for selection
      let disabled = false;
      // if (rootBranch !== undefined) {
      //   if (hasChildren) {
      //     // Folders are disabled for selection.
      //     // We only allow selecting leaf fields.
      //     disabled = true;
      //   } else {
      //     // Leaf must share the same root branch (top-level parent) and should be at the same level
      //     const nodeRootBranch = currentPath.split(".")[0];
      //     //const nodeDepth = currentPath.split(".").length;
      //     //const allowedDepth = rootBranch.split(".").length + 1;
      //     disabled = (nodeRootBranch !== rootBranch); //|| (nodeDepth !== allowedDepth);
      //   }
      // } else if (hasChildren) {
      //   // Even if nothing is selected, we don't allow selecting folders (objects)
      //   // because we want to select specific fields for vector/metadata.
      //   disabled = true;
      // }

      return {
        id: currentPath,
        name: element.name,
        path: currentPath,
        type: hasChildren ? "folder" : "leaf",
        disabled,
        children: hasChildren
          ? this.buildFieldTreeData(element.elementTypeModel!.elements!, currentPath, rootBranch)
          : undefined
      } as ITreeSelectNode;
    });
  };

  /**
   * Get field tree data for the selected action instance
   */
  getFieldTreeData = (): ITreeSelectNode[] => {
    if (!this.state.selectedActionId) return [];

    // selectedActionId is now action instance ID, get the action definition
    const actionInstance = this.getSelectableActionInstances().find((a) => a.id === this.state.selectedActionId);
    if (!actionInstance) return [];

    const actionDef = this.getActionDefinitionForInstance(actionInstance);
    if (!actionDef?.returnTypeModel?.elements) return [];

    // Calculate root branch based on current selection across all field types
    const { vectorFields, metadataFields, primaryKeys } = this.state;
    const allSelected = [...vectorFields, ...metadataFields, ...primaryKeys];

    let rootBranch: string | undefined;
    if (allSelected.length > 0) {
      const firstSelected = allSelected[0];
      rootBranch = firstSelected.split(".")[0];
    }

    return this.buildFieldTreeData(actionDef.returnTypeModel.elements, "", rootBranch);
  };

  /**
   * Handle action selection change - now uses action instance ID
   */
  handleActionChange = (actionInstanceId: string) => {
    const actionInstance = this.getSelectableActionInstances().find((a) => a.id === actionInstanceId);
    if (!actionInstance) return;

    const actionDef = this.getActionDefinitionForInstance(actionInstance);
    if (!actionDef) return;

    const resultType = this.classifyActionResultType(actionDef);

    // Reset configuration when action changes - don't auto-save
    this.setState({
      selectedActionId: actionInstanceId,
      selectedActionResultType: resultType,
      vectorFields: [],
      metadataFields: [],
      primaryKeys: [],
      jqSchema: "",
      metadataJqSchema: "",
      isSaved: false,
      validationError: null
    });
  };

  /**
   * Handle delete strategy change
   */
  handleDeleteStrategyChange = (strategy: EDeleteStrategy) => {
    // Clear primary keys if switching to CleanAndInsert
    if (strategy === EDeleteStrategy.CleanAndInsert) {
      this.setState({
        deleteStrategy: strategy,
        primaryKeys: [],
        isSaved: false,
        validationError: null
      });
    } else {
      this.setState({
        deleteStrategy: strategy,
        isSaved: false,
        validationError: null
      });
    }
  };

  /**
   * Handle vector fields selection change
   */
  handleVectorFieldsChange = (fields: string[]) => {
    this.setState({ vectorFields: fields, isSaved: false, validationError: null });
  };

  /**
   * Handle metadata fields selection change
   */
  handleMetadataFieldsChange = (fields: string[]) => {
    this.setState({ metadataFields: fields, isSaved: false, validationError: null });
  };

  /**
   * Handle primary keys selection change
   */
  handlePrimaryKeysChange = (keys: string[]) => {
    this.setState({ primaryKeys: keys, isSaved: false, validationError: null });
  };

  /**
   * Build separate JQ schemas for vector fields and metadata fields
   * Example:
   * Vector fields: ".[] | {id, name, description}" - will be embedded
   * Metadata fields: ".[] | {status, createdAt}" - stored but not embedded
   */
  updateJqSchema = () => {
    const { vectorFields, metadataFields, selectedActionResultType } = this.state;

    if (selectedActionResultType === EActionResultType.String) {
      this.setState({ jqSchema: ".", metadataJqSchema: "" });
      return;
    }

    // Build JQ schema for vector fields (to be embedded)
    let jqSchema = "";
    if (vectorFields.length > 0) {
      const vectorFieldsStr = vectorFields.join(", ");
      jqSchema = `.[] | {${vectorFieldsStr}}`;
    }

    // Build JQ schema for metadata fields (stored but not embedded)
    let metadataJqSchema = "";
    if (metadataFields.length > 0) {
      const metadataFieldsStr = metadataFields.join(", ");
      metadataJqSchema = `.[] | {${metadataFieldsStr}}`;
    }

    this.setState({ jqSchema, metadataJqSchema });
  };

  /**
   * Build JQ schemas from current field selections
   */
  buildJqSchemas = (): { jqSchema: string; metadataJqSchema: string } => {
    const { vectorFields, metadataFields, selectedActionResultType } = this.state;

    if (selectedActionResultType === EActionResultType.String) {
      return { jqSchema: ".", metadataJqSchema: "" };
    }

    // Build JQ schema for vector fields (to be embedded)
    let jqSchema = "";
    if (vectorFields.length > 0) {
      const vectorFieldsStr = vectorFields.join(", ");
      jqSchema = `.[] | {${vectorFieldsStr}}`;
    }

    // Build JQ schema for metadata fields (stored but not embedded)
    let metadataJqSchema = "";
    if (metadataFields.length > 0) {
      const metadataFieldsStr = metadataFields.join(", ");
      metadataJqSchema = `.[] | {${metadataFieldsStr}}`;
    }

    return { jqSchema, metadataJqSchema };
  };

  /**
   * Validate and return error message if invalid
   */
  getValidationError = (): string | null => {
    const { selectedActionId, selectedActionResultType, deleteStrategy, vectorFields, metadataFields, primaryKeys } =
      this.state;

    if (!selectedActionId) {
      return "Please select an action";
    }

    if (selectedActionResultType === EActionResultType.Object && vectorFields.length === 0) {
      return "At least one vector field is required";
    }

    if (deleteStrategy === EDeleteStrategy.InsertOrUpdate && primaryKeys.length === 0) {
      return "Primary keys are required for Insert or Update strategy";
    }

    // Check for branch consistency (all selected fields must share the same top-level parent)
    const allSelected = [...vectorFields, ...metadataFields, ...primaryKeys];
    if (allSelected.length > 1) {
      const rootBranches = allSelected.map((f) => f.split(".")[0]);
      const uniqueRootBranches = new Set(rootBranches);
      // if (uniqueRootBranches.size > 1) {
      //   return "All selected fields (vector, metadata, and primary keys) must belong to the same top-level branch.";
      // }
    }

    return null;
  };

  /**
   * Handle Save button click - validates and saves all parameters
   */
  handleSave = () => {
    const validationError = this.getValidationError();

    if (validationError) {
      this.setState({ validationError });
      return;
    }

    // Build JQ schemas and update state, then save
    const { jqSchema, metadataJqSchema } = this.buildJqSchemas();
    this.setState({ jqSchema, metadataJqSchema, isSaved: true, validationError: null }, () => {
      this.updateActionParameters();
    });
  };

  /**
   * Update action parameters with current state
   */
  updateActionParameters = () => {
    const params = _.cloneDeep(this.props.parameters);

    // Get the selected action instance (selectedActionId is now instance ID)
    const selectedActionInstance = this.getSelectableActionInstances().find(
      (a) => a.id === this.state.selectedActionId
    );

    // Get the action definition for the selected instance
    const selectedActionDef = selectedActionInstance
      ? this.getActionDefinitionForInstance(selectedActionInstance)
      : undefined;

    // Update actionId - backend needs the action DEFINITION ID, not instance ID
    const actionIdParam = params.find((p) => p.name === "actionId");
    if (actionIdParam && selectedActionInstance) {
      actionIdParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: selectedActionInstance.actionDefinitionId || "",
        description: ""
      };
    }

    // Update name with format: <project-name>_<page-name>_<action-tag>
    // Use the action instance's tag (user-assigned) or fall back to instance ID
    const nameParam = params.find((p) => p.name === "name");
    if (nameParam && selectedActionInstance) {
      const projectName = this.props.sd.project?.name || "";
      const pageName = this.props.sd.sdName || "";
      const actionTag = selectedActionInstance.tag || selectedActionInstance.id || "";
      const sourceName = `${projectName}_${pageName}_${actionTag}`;

      nameParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: sourceName,
        description: ""
      };
    }

    // Update actionName
    const actionNameParam = params.find((p) => p.name === "actionName");
    if (actionNameParam && selectedActionDef) {
      actionNameParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: selectedActionDef.name,
        description: ""
      };
    }

    // Update actionResult - reference to the selected action's result
    const actionResultParam = params.find((p) => p.name === "actionResult");
    if (actionResultParam && selectedActionDef && selectedActionInstance) {
      actionResultParam.symbol = {
        category: ESymbolCategory.actionResult,
        label: "Action Result",
        actionDefinitionId: selectedActionDef.id,
        description: `[${selectedActionDef.name}]`,
        currentType: (selectedActionDef.returnTypeModel?.name || "Unknown") as unknown as BackEndValueType,
        targetType: (selectedActionDef.returnTypeModel?.name || "Unknown") as unknown as BackEndValueType,
        resultActionId: selectedActionInstance.id
      };
    }

    // Update deleteOlderRecords (boolean as string)
    const deleteOlderRecordsParam = params.find((p) => p.name === "deleteOlderRecords");
    if (deleteOlderRecordsParam) {
      deleteOlderRecordsParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: (this.state.deleteStrategy === EDeleteStrategy.CleanAndInsert).toString(),
        description: ""
      };
    }

    // Update jQSchema
    const jqSchemaParam = params.find((p) => p.name === "jQSchema");
    if (jqSchemaParam) {
      jqSchemaParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: this.state.jqSchema,
        description: ""
      };
    }

    // Update primaryKeys (comma-separated string)
    const primaryKeysParam = params.find((p) => p.name === "primaryKeys");
    if (primaryKeysParam) {
      primaryKeysParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: this.state.primaryKeys.join(","),
        description: ""
      };
    }

    // Update metadataJQSchema (separate schema for metadata fields)
    const metadataJqSchemaParam = params.find((p) => p.name === "metadataJQSchema");
    if (metadataJqSchemaParam) {
      metadataJqSchemaParam.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: this.state.metadataJqSchema,
        description: ""
      };
    }

    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    this.props.updateValues(valuesToUpdate);
  };

  /**
   * Handle symbol picker value change for standard parameters
   */
  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const params = _.cloneDeep(this.props.parameters);
    const param = params.find((p) => p.name === parameter.name);

    if (param) param.symbol = symbol;

    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  /**
   * Check if form is valid for submission
   */
  isValid = (): boolean => {
    const { selectedActionId, selectedActionResultType, deleteStrategy, vectorFields, primaryKeys } = this.state;

    // Must have action selected
    if (!selectedActionId) return false;

    // Object results need at least one vector field
    if (selectedActionResultType === EActionResultType.Object && vectorFields.length === 0) {
      return false;
    }

    // InsertOrUpdate requires primary keys
    if (deleteStrategy === EDeleteStrategy.InsertOrUpdate && primaryKeys.length === 0) {
      return false;
    }

    return true;
  };

  /**
   * Render standard parameter with SymbolPicker
   */
  renderStandardParameter = (parameter: IActionParameter, selectboxParameter?: SelectboxInputParameter) => {
    if (parameter.isOptionalParameter && !parameter.isExternalVisible) return null;
    if (selectboxParameter?.isDynamic === true) return null;

    return (
      <div
        className={parameter.isDeletedOnSource === true ? "isDeletedOnSource" : ""}
        key={`saveActionResult-${this.props.action.id}-${parameter.name}`}
      >
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle parameter={parameter} showTypeChips={selectboxParameter === undefined} />
        </span>
        <SymbolPicker
          changeType="onBlur"
          symbol={parameter.symbol}
          valueType={parameter.Typename}
          targetCardinality={parameter.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(parameter, symbol, changeReason)
          }
          parameterOptions={selectboxParameter?.options}
          bindingParameter={parameter}
        />
      </div>
    );
  };

  /**
   * Render action selector dropdown - shows action instances with their tags
   */
  renderActionSelector = () => {
    const actionInstances = this.getSelectableActionInstances();

    return (
      <div className="save-action-result-section">
        <span className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: 6, marginTop: 8 }}>
          Select Action
          <KuikaTooltip content="Select an action whose result will be stored in the vector database. Only actions returning objects or strings are available.">
            <span>
              <InfoSvg style={{ marginLeft: 4, width: 14, height: 14 }} />
            </span>
          </KuikaTooltip>
        </span>
        <KuikaSelectbox
          style={{ width: "100%" }}
          placeholder="Select an action"
          value={this.state.selectedActionId}
          onChange={this.handleActionChange}
          searchable
          options={actionInstances.map((instance) => {
            const actionDef = this.getActionDefinitionForInstance(instance);
            const displayName = actionDef?.displayName || actionDef?.name || instance.label || "Unknown Action";
            const tagSuffix = instance.tag ? ` (tag: ${instance.tag})` : "";
            return {
              value: instance.id,
              label: `${displayName}${tagSuffix}`
            };
          })}
        />
      </div>
    );
  };

  /**
   * Render delete strategy selector using KuikaTabs
   */
  renderDeleteStrategySelector = () => {
    return (
      <div className="save-action-result-section">
        <span className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: 6, marginTop: 8 }}>
          Update Strategy
          <KuikaTooltip content="Clean & Insert: Deletes all existing vectors for this action before inserting. Insert or Update: Uses primary keys to update existing records or insert new ones.">
            <span>
              <InfoSvg style={{ marginLeft: 4, width: 14, height: 14 }} />
            </span>
          </KuikaTooltip>
        </span>
        <KuikaTabs
          value={this.state.deleteStrategy}
          onChange={(value) => this.handleDeleteStrategyChange(value as EDeleteStrategy)}
          options={[
            { label: "Clean & Insert", value: EDeleteStrategy.CleanAndInsert },
            { label: "Insert or Update", value: EDeleteStrategy.InsertOrUpdate }
          ]}
        />
      </div>
    );
  };

  /**
   * Render vector fields selector (only for Object results)
   */
  renderVectorFieldsSelector = () => {
    if (this.state.selectedActionResultType !== EActionResultType.Object) return null;

    return (
      <KuikaTreeSelect
        treeData={this.getFieldTreeData()}
        value={this.state.vectorFields}
        onChange={this.handleVectorFieldsChange}
        label="Vector Fields"
        labelTooltip="Select fields that will be converted into vector embeddings for semantic search."
        // errorMessage={this.state.vectorFields.length === 0 ? "At least one vector field is required" : undefined}
        showClearAll
        showTags
      />
    );
  };

  /**
   * Render metadata fields selector (only for Object results)
   */
  renderMetadataFieldsSelector = () => {
    if (this.state.selectedActionResultType !== EActionResultType.Object) return null;

    return (
      <KuikaTreeSelect
        treeData={this.getFieldTreeData()}
        value={this.state.metadataFields}
        onChange={this.handleMetadataFieldsChange}
        label="Metadata Fields (Optional)"
        labelTooltip="Select fields that will be stored as metadata alongside vectors. These are not embedded but can be used for filtering."
        showClearAll
        showTags
      />
    );
  };

  /**
   * Render primary keys selector (only for InsertOrUpdate strategy with Object results)
   */
  renderPrimaryKeysSelector = () => {
    if (this.state.deleteStrategy !== EDeleteStrategy.InsertOrUpdate) return null;
    if (this.state.selectedActionResultType !== EActionResultType.Object) return null;

    return (
      <KuikaTreeSelect
        treeData={this.getFieldTreeData()}
        value={this.state.primaryKeys}
        onChange={this.handlePrimaryKeysChange}
        label="Primary Keys"
        labelTooltip="Select fields that uniquely identify each record. Used for determining whether to update or insert."
        // errorMessage={
        //   this.state.primaryKeys.length === 0 ? "Primary keys are required for Insert or Update strategy" : undefined
        // }
        showClearAll
        showTags
      />
    );
  };

  /**
   * Render JQ Schema preview (read-only, auto-generated)
   */
  renderJqSchemaPreview = () => {
    if (this.state.selectedActionResultType !== EActionResultType.Object) return null;
    if (!this.state.jqSchema) return null;

    return (
      <div className="save-action-result-section">
        <span className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: 6, marginTop: 8 }}>
          JQ Schema (Auto-generated)
          <KuikaTooltip content="This JQ schema is automatically generated based on your field selections.">
            <span>
              <InfoSvg style={{ marginLeft: 4, width: 14, height: 14 }} />
            </span>
          </KuikaTooltip>
        </span>
        <div
          style={{
            padding: 8,
            background: "var(--dark-1)",
            borderRadius: "var(--radius-l)",
            fontFamily: "monospace",
            fontSize: 12,
            wordBreak: "break-all"
          }}
        >
          {this.state.jqSchema}
        </div>
      </div>
    );
  };

  /**
   * Render string result info (when action returns string)
   */
  renderStringResultInfo = () => {
    if (this.state.selectedActionResultType !== EActionResultType.String) return null;

    return (
      <div
        className="save-action-result-section"
        style={{
          padding: 12,
          background: "var(--blue-05)",
          borderRadius: "var(--radius-l)",
          marginTop: 8
        }}
      >
        <span style={{ fontSize: 12 }}>
          This action returns a string. The entire string will be vectorized as a single document. No field selection is
          needed.
        </span>
      </div>
    );
  };

  /**
   * Check if metadata fields selector should be shown
   * This is shown when the metadataJQSchema optional parameter is enabled
   */
  isMetadataFieldsSelectorVisible = (): boolean => {
    const metadataParam = this.props.parameters.find((p) => p.name === "metadataJQSchema");
    return metadataParam?.isExternalVisible === true;
  };

  render(): ReactNode {
    // Render standard parameters that aren't handled by custom UI
    // These are handled by custom selectors: actionId, actionName, deleteOlderRecords, jQSchema, primaryKeys, metadataJQSchema
    const standardParams = this.props.parameters.filter(
      (p) =>
        ![
          "actionId",
          "actionName",
          "deleteOlderRecords",
          "jQSchema",
          "primaryKeys",
          "actionResult",
          "metadataJQSchema",
          "name"
        ].includes(p.name)
    );

    return (
      <div className="save-action-result-symbols-group">
        {/* Action Selector */}
        {this.renderActionSelector()}

        {/* Show remaining UI only when action is selected */}
        {this.state.selectedActionId && (
          <>
            {/* Delete Strategy */}
            {this.renderDeleteStrategySelector()}

            {/* String result info */}
            {this.renderStringResultInfo()}

            {/* Vector Fields (Object only) */}
            {this.renderVectorFieldsSelector()}

            {/* Metadata Fields (Object only) - shown when metadataJQSchema param is enabled via optional params */}
            {this.isMetadataFieldsSelectorVisible() && this.renderMetadataFieldsSelector()}

            {/* Primary Keys (InsertOrUpdate + Object only) */}
            {this.renderPrimaryKeysSelector()}

            {/* JQ Schema Preview NOT DOESN'T HAVE TO BE SHOWN TO THE USER */}
            {/* {this.renderJqSchemaPreview()} */}

            {/* Standard parameters */}
            {standardParams.map((param) => this.renderStandardParameter(param))}

            {/* Validation Error */}
            {this.state.validationError && (
              <div
                style={{
                  padding: "8px 12px",
                  background: "var(--callout-red-fill)",
                  border: "1px solid var(--callout-red-line)",
                  borderRadius: "var(--radius-l)",
                  marginTop: 12,
                  color: "var(--tone-red-fg)",
                  fontSize: 12
                }}
              >
                {this.state.validationError}
              </div>
            )}

            {/* Save Button */}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              {!this.state.isSaved && (
                <span style={{ fontSize: 11, color: "var(--yellow-7)", alignSelf: "center" }}>Unsaved changes</span>
              )}
              <KuikaButton type="primary" size="sm" onClick={this.handleSave} disabled={this.state.isSaved}>
                {this.state.isSaved ? "Saved" : "Save"}
              </KuikaButton>
            </div>
          </>
        )}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    );
  };
}

const saveActionResultSymbolsGroup = withSD<ISaveActionResultSymbolsGroupProps>(SaveActionResultSymbolsGroup);
export { saveActionResultSymbolsGroup as SaveActionResultSymbolsGroup };
